import StudentDetailExpandedView from './StudentList/StudentDetailExpandedView';
import StudentDetailInlineEdit from './StudentList/StudentDetailInlineEdit';
import EnrollmentStatusControl from './StudentList/EnrollmentStatusControl';
import { BULK_DELETE_LIMIT } from '../utils/bulkStudentSelection';
import { hasLeftEnrollment, LEFT_STATUS } from '../utils/studentEnrollmentStatus';

const StudentList = ({
  students,
  expandedId,
  toggleExpand,
  expandedStudentDetails = {},
  expandedDetailLoadingId = null,
  onDeleteStudent,
  onToggleSelection,
  selectedStudents = [],
  onBulkDelete,
  showBulkActions = false,
  selectionLimit = BULK_DELETE_LIMIT,
  selectionAtLimit = false,
  selectionWarning = '',
  showStatusControls = false,
  onEnrollmentStatusChange,
  onBulkMarkAsLeft,
  statusUpdatingIds = new Set(),
  bulkStatusUpdating = false,
  showInlineEdit = false,
  canEditStudents = true,
  canDeleteStudents = true,
  editingStudentId = null,
  onStartEdit,
  onCancelEdit,
  onSaveInlineEdit,
  inlineEditSavingId = null,
  inlineEditError = null,
  inlineEditToken = null,
  onOpenFullEdit,
  onChangeBatch,
  onChangePassword,
}) => {
  if (students.length === 0) return <p>No users match the filter.</p>;

  const leftStudentCount = students.filter(hasLeftEnrollment).length;
  const allowInlineEdit = showInlineEdit && canEditStudents;
  const allowDelete = Boolean(onDeleteStudent) && canDeleteStudents;
  const allowBulkActions = showBulkActions && (canDeleteStudents || (showStatusControls && canEditStudents));
  const allowStatusControls = showStatusControls && canEditStudents;

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-blue-800 font-medium">
          📊 Showing {students.length} user{students.length !== 1 ? 's' : ''}
          {students.length > 0 && (() => {
            const userTypes = [...new Set(students.map(s => s.userType || s.role || 'student'))];
            return userTypes.length > 0 ? ` (${userTypes.join(', ')})` : '';
          })()}
        </p>
        {allowBulkActions && canDeleteStudents && (
          <p className="text-xs text-amber-800 mt-2">
            Bulk delete: select up to {selectionLimit} students at a time, then delete. Repeat for larger lists.
          </p>
        )}
        {students.length > 0 && (
          <p className="text-xs text-blue-600 mt-1">
            Click on any user to view detailed information
          </p>
        )}
        {leftStudentCount > 0 && (
          <p className="text-xs text-amber-800 mt-2">
            {leftStudentCount} student{leftStudentCount !== 1 ? 's' : ''} marked{' '}
            <span className="font-medium">{LEFT_STATUS}</span> are highlighted in amber.
          </p>
        )}
        {allowInlineEdit && (
          <p className="text-xs text-gray-600 mt-2">
            Expand a student and click <strong>Edit</strong> to update details inline, or use{' '}
            <strong>Full edit</strong> for the complete form.
          </p>
        )}
        {allowStatusControls && (
          <p className="text-xs text-gray-600 mt-2">
            Update <strong>Student Status</strong> from each row, or select students and use{' '}
            <strong>Mark as Left</strong>.
          </p>
        )}
      </div>

      {selectionWarning && allowBulkActions && (
        <p className="text-sm text-amber-900 bg-amber-100 border border-amber-300 rounded-md px-3 py-2 mb-4">
          {selectionWarning}
        </p>
      )}

      {allowBulkActions && selectedStudents.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 rounded-lg mb-4">
          <span className="text-sm font-medium text-blue-800">
            {selectedStudents.length} / {selectionLimit} user{selectedStudents.length !== 1 ? 's' : ''} selected
          </span>
          {allowStatusControls && onBulkMarkAsLeft && (
            <button
              type="button"
              onClick={onBulkMarkAsLeft}
              disabled={bulkStatusUpdating}
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkStatusUpdating ? 'Updating…' : 'Mark as Left'}
            </button>
          )}
          {allowDelete && onBulkDelete && (
            <button
              onClick={onBulkDelete}
              disabled={selectedStudents.length > selectionLimit}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete Selected
            </button>
          )}
          <button
            onClick={() => onToggleSelection(null)}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
          >
            Clear Selection
          </button>
        </div>
      )}

      {students.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No users found matching the filter criteria.</p>
          <p className="text-sm mt-2">Try adjusting your search filters.</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {students.map((s) => {
            const rowSelected = selectedStudents.some((selected) => selected.id === s.id);
            const rowCheckboxDisabled = showBulkActions && selectionAtLimit && !rowSelected;
            const isLeftStudent = hasLeftEnrollment(s);

            return (
            <li
              key={s.id || s._id}
              className={`border rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                isLeftStudent
                  ? 'border-amber-300 bg-amber-50 border-l-4 border-l-amber-500'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="p-4">
                <div className="flex justify-between items-center">
                  {allowBulkActions && (
                    <div className="mr-3">
                      <input
                        type="checkbox"
                        checked={rowSelected}
                        disabled={rowCheckboxDisabled}
                        title={rowCheckboxDisabled ? `Maximum ${selectionLimit} students can be selected` : undefined}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleSelection(s);
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                      />
                    </div>
                  )}

                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => toggleExpand(s.id || s._id)}
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {s.fullName || s.full_name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.id || 'N/A'}
                      {isLeftStudent && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium rounded bg-amber-200 text-amber-900">
                          Left
                        </span>
                      )}
                      {(s.userType && s.userType !== 'student') && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800">
                          {s.userType || s.role}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">{s.emailAddress || s.email_address || s.email || 'N/A'}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(s.userType && s.userType !== 'student') ? (
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">
                          {s.userType || s.role}
                        </span>
                      ) : (
                        <>
                          {s.enrollments && s.enrollments.length > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" title="Open row for full enrollment details">
                              {s.enrollments.length} enrollment{s.enrollments.length !== 1 ? 's' : ''}
                              {s.enrollments[0]?.batch?.course?.category?.categoryName
                                ? ` · ${s.enrollments[0].batch.course.category.categoryName}`
                                : ''}
                            </span>
                          )}
                          {s.academicQualifications && s.academicQualifications.length > 0 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {s.academicQualifications[0].qualificationType || s.academicQualifications[0].qualification_type}
                            </span>
                          )}
                          {s.currentAddress && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                              📍 {s.currentAddress}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1 ml-4">
                    {onChangeBatch && (!s.userType || s.userType === 'student') && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangeBatch(s);
                        }}
                        disabled={!s.enrollments?.length}
                        className="text-green-700 hover:text-green-900 text-sm font-medium px-2 py-1 rounded hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          s.enrollments?.length
                            ? 'Close current enrollment and enroll in another batch'
                            : 'No enrollment to move'
                        }
                      >
                        Change batch
                      </button>
                    )}
                    {onChangePassword && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangePassword(s);
                        }}
                        className="text-slate-700 hover:text-slate-900 text-sm font-medium px-2 py-1 rounded hover:bg-slate-50"
                        title="Set a new login password"
                      >
                        Password
                      </button>
                    )}
                    {allowStatusControls && onEnrollmentStatusChange && (!s.userType || s.userType === 'student') && (
                      <EnrollmentStatusControl
                        student={s}
                        onStatusChange={onEnrollmentStatusChange}
                        updating={statusUpdatingIds.has(s.id || s._id)}
                      />
                    )}
                    {allowInlineEdit && (!s.userType || s.userType === 'student') && onOpenFullEdit && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenFullEdit(s);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium px-2 py-1 rounded hover:bg-indigo-50"
                      >
                        Full edit
                      </button>
                    )}
                    {allowInlineEdit && (!s.userType || s.userType === 'student') && onStartEdit && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingStudentId === (s.id || s._id)) {
                            onCancelEdit?.();
                          } else {
                            onStartEdit(s.id || s._id);
                            if (expandedId !== (s.id || s._id)) {
                              toggleExpand(s.id || s._id);
                            }
                          }
                        }}
                        className="text-blue-600 hover:text-blue-900 text-sm font-medium px-2 py-1 rounded hover:bg-blue-50"
                      >
                        {editingStudentId === (s.id || s._id) ? 'Cancel edit' : 'Edit'}
                      </button>
                    )}
                    {allowDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteStudent(s);
                        }}
                        className="text-red-600 hover:text-red-900 text-sm font-medium px-2 py-1 rounded hover:bg-red-50"
                      >
                        Delete
                      </button>
                    )}
                    <span className="text-blue-600 font-bold text-2xl cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(s.id || s._id);
                          }}>
                      {expandedId === (s.id || s._id) ? '−' : '+'}
                </span>
                  </div>
                </div>

                {expandedId === (s.id || s._id) && (
                  expandedDetailLoadingId === (s.id || s._id) ? (
                    <div className="mt-6 border-t pt-6 px-4 text-sm text-gray-500">
                      Loading full student details…
                    </div>
                  ) : allowInlineEdit &&
                    editingStudentId === (s.id || s._id) &&
                    onSaveInlineEdit ? (
                    <StudentDetailInlineEdit
                      student={expandedStudentDetails[s.id || s._id] || s}
                      token={inlineEditToken}
                      saving={inlineEditSavingId === (s.id || s._id)}
                      error={
                        editingStudentId === (s.id || s._id) ? inlineEditError : null
                      }
                      onCancel={() => onCancelEdit?.()}
                      onSave={(formData, helpers) =>
                        onSaveInlineEdit(expandedStudentDetails[s.id || s._id] || s, formData, helpers)
                      }
                    />
                  ) : (
                    <StudentDetailExpandedView
                      student={expandedStudentDetails[s.id || s._id] || s}
                      className="mt-6 border-t pt-6 px-4"
                    />
                  )
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default StudentList;
